import { useState, useEffect, FormEvent } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { apiFetch } from '@/lib/apiFetch'
import { timArt } from '@/lib/timArt'
import { fetchMagnumMoment } from '@/lib/magnumMoment'

export default function LoginPage() {
  const { unifiedLogin, user } = useAuth()
  const history = useHistory()
  const location = useLocation()
  const redirectTo = new URLSearchParams(location.search).get('redirect')
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [sendingMagicLink, setSendingMagicLink] = useState(false)
  // Tim guards the door. Fallbacks stand in if the engine is unreachable —
  // an empty bubble at the checkpoint reads as broken.
  const [doorLine, setDoorLine] = useState('I await your credentials.')
  const [granted, setGranted] = useState<string | null>(null)

  useEffect(() => {
    fetchMagnumMoment({ contextType: 'LOGIN_PROMPT', streak: 0 })
      .then((m) => { if (m.line?.text) setDoorLine(m.line.text) })
      .catch(() => {})
  }, [])

  // Let the greeting land before leaving the page.
  useEffect(() => {
    if (!granted) return
    const t = setTimeout(() => setGranted(null), 1600)
    return () => clearTimeout(t)
  }, [granted])

  // Hold the redirect while Tim is still speaking.
  if (user && !granted) {
    history.replace(redirectTo || (user.type === 'parent' ? '/dashboard' : '/practice'))
    return null
  }

  const handleMagicLink = async () => {
    if (!identifier || !identifier.includes('@')) {
      setError('Enter your email address to receive a magic link.')
      return
    }
    setError('')
    setSendingMagicLink(true)
    try {
      await apiFetch('/app/magic-link', {
        method: 'POST',
        body: JSON.stringify({ email: identifier }),
      })
      setMagicLinkSent(true)
    } catch (err: any) {
      setError(err.message || 'Failed to send magic link.')
    } finally {
      setSendingMagicLink(false)
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await unifiedLogin(identifier, password)
      setGranted('Access granted.')
      fetchMagnumMoment({ contextType: 'LOGIN_GRANTED', streak: 0 })
        .then((m) => { if (m.line?.text) setGranted(m.line.text) })
        .catch(() => {})
    } catch (err: any) {
      setError(err.message || 'Login failed. Check your credentials.')
    } finally {
      setSubmitting(false)
    }
  }

  if (granted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm space-y-4 text-center animate-in fade-in duration-300">
          <img src={timArt('happy')} alt="Tim" className="w-28 h-28 mx-auto object-contain" />
          <p className="text-xl font-black text-primary">{granted}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <img
            src={timArt('neutral')}
            alt="Tim"
            className="w-24 h-24 mx-auto object-contain mb-4"
          />
          <h1 className="text-3xl font-black text-primary">Log In</h1>
          <p className="text-sm text-muted-foreground italic mt-2">"{doorLine}"</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="identifier" className="block text-sm font-medium mb-1">
              Email or Username
            </label>
            <input
              id="identifier"
              type="text"
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50"
          >
            {submitting ? 'Logging in...' : 'Log In'}
          </button>

          <div className="text-right">
            <Link to="/reset-password" className="text-sm text-primary hover:underline">
              Forgot password?
            </Link>
          </div>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">or</span>
          </div>
        </div>

        {magicLinkSent ? (
          <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm text-center">
            Check your email for a login link!
          </div>
        ) : (
          <button
            type="button"
            onClick={handleMagicLink}
            disabled={sendingMagicLink}
            className="w-full py-2.5 border border-border rounded-lg font-semibold text-sm hover:bg-muted transition disabled:opacity-50"
          >
            {sendingMagicLink ? 'Sending...' : 'Send a magic link'}
          </button>
        )}

        <div className="text-center text-sm">
          <p className="text-muted-foreground">
            New here?{' '}
            <Link to="/signup" className="text-primary font-medium hover:underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
